import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Trophy as TrophyIcon, Share2, Check, Calendar as CalendarIcon, Lock, ChevronLeft, ChevronRight,
  Flame, Shield, AlertTriangle, ShoppingCart, Medal,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import GameCanvas from "../game/GameCanvas";
import api from "../lib/api";
import { generateDailyLevel } from "../lib/levels";

function monthKey(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function todayKey() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

export default function Daily() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  });
  const [calendar, setCalendar] = useState({ month: "", days: [] });
  const [lastScore, setLastScore] = useState(0);
  const [copied, setCopied] = useState(false);
  // Streak + shield state from /api/achievements stats. `daily_streak` is the
  // current consecutive-day count, `streak_shields` is the unspent miss-day
  // shield charges (earned via the 14-day Streak Shield achievement).
  const [streak, setStreak] = useState({ daily_streak: 0, streak_shields: 0 });
  // Public-leaderboard rank (computed from `stats.daily_streak`). `rank` is
  // null when the pilot has no streak yet ("Off the board · X total" then).
  const [streakRank, setStreakRank] = useState({ rank: null, total_streakers: 0 });
  // Daily mid-attempt checkpoint — surfaces a "CONTINUE" CTA when the
  // pilot has a saved lives/score from earlier today (set after every
  // life loss by GameCanvas in daily mode). Cleared on natural pass
  // via /daily/submit, on a different UTC day, or on explicit dismiss.
  // Bricks reset on resume (we don't serialize physics state) — pilot
  // restarts the level fresh but keeps their lives + accumulated score.
  const [dailyCheckpoint, setDailyCheckpoint] = useState(null);
  const [dailyCheckpointDismissed, setDailyCheckpointDismissed] = useState(false);
  const [canvasResumeKey, setCanvasResumeKey] = useState(0);

  const refreshStreak = useCallback(() => {
    api.get("/achievements")
      .then((r) => {
        const s = r.data?.stats || {};
        setStreak({
          daily_streak: Number(s.daily_streak || 0),
          streak_shields: Number(s.streak_shields || 0),
        });
      })
      .catch(() => {});
    api.get("/leaderboard/my-streak-rank")
      .then((r) => setStreakRank({
        rank: r.data?.rank ?? null,
        total_streakers: Number(r.data?.total_streakers || 0),
      }))
      .catch(() => {});
  }, []);
  useEffect(() => { refreshStreak(); }, [refreshStreak]);

  const refreshToday = useCallback(() => {
    api.get("/daily/today").then((r) => setData(r.data)).catch(() => {
      const iso = todayKey();
      setData({ seed: iso, leaderboard: [], passed: false, locked: false, best: 0, attempts: 0 });
    });
  }, []);
  useEffect(() => { refreshToday(); }, [refreshToday]);

  const refreshCalendar = useCallback(() => {
    const m = monthKey(calMonth);
    api.get(`/daily/calendar?month=${m}`).then((r) => setCalendar(r.data)).catch(() => {
      setCalendar({ month: m, days: [] });
    });
  }, [calMonth]);
  useEffect(() => { refreshCalendar(); }, [refreshCalendar]);

  // Fetch any saved mid-attempt daily checkpoint on mount. Server
  // auto-discards stale (cross-day) and post-pass checkpoints so we
  // always get the right answer.
  useEffect(() => {
    api.get("/daily/checkpoint")
      .then((r) => setDailyCheckpoint(r.data?.checkpoint || null))
      .catch(() => setDailyCheckpoint(null));
  }, []);
  // Resume CTA gate: a non-null checkpoint exists AND the pilot
  // hasn't already passed today AND they haven't dismissed it yet.
  const showDailyResumeCta =
    !!dailyCheckpoint &&
    !dailyCheckpointDismissed &&
    !!data &&
    !data.locked &&
    !data.passed;
  const resumeFromDailyCheckpoint = useCallback(() => {
    if (!dailyCheckpoint) return;
    // Bump the canvas key so it remounts and the first-mount init
    // picks up `dailyStartLives` + `dailyStartScore`.
    setCanvasResumeKey((k) => k + 1);
    setDailyCheckpointDismissed(true);
  }, [dailyCheckpoint]);
  const dismissDailyCheckpoint = useCallback(() => {
    setDailyCheckpointDismissed(true);
    api.delete("/daily/checkpoint").catch(() => {});
    setDailyCheckpoint(null);
  }, []);

  const dailyLevel = useMemo(() => (data ? generateDailyLevel(data.seed) : null), [data]);

  const onComplete = async (score, meta = {}) => {
    setLastScore(score);
    try {
      const r = await api.post("/daily/submit", { score });
      setData((prev) => ({
        ...(prev || {}),
        seed: r.data.seed,
        leaderboard: r.data.leaderboard,
        passed: true,
        locked: true,
        best: r.data.best,
        attempts: r.data.attempts,
      }));
      refreshCalendar();
      refreshStreak();
    } catch (_) { /* noop */ }
    // Trick-Or-Treat speed-clear bonus — fire only during October, and
    // only when the pilot cleared the daily in under 60s. Server is
    // authoritative on both date + reward tier; toast the reward on
    // success. Silently ignore any error so the daily-submit UX isn't
    // interrupted by a bonus-path hiccup.
    const clearMs = Number(meta?.duration_ms || 0);
    if (clearMs > 0 && clearMs <= 60_000) {
      try {
        const claim = await api.post("/trick-or-treat/claim", { clear_time_ms: clearMs });
        const reward = Number(claim?.data?.reward || 0);
        const already = !!claim?.data?.already_claimed;
        if (reward > 0 && !already) {
          const seconds = Math.max(1, Math.round(clearMs / 1000));
          toast(`Trick or Treat! +${reward} moonstones`, {
            description: `Cleared in ${seconds}s — tipped to your wallet.`,
            duration: 4200,
          });
        }
      } catch (_e) { /* event inactive or not eligible — silent */ }
    }
  };

  // Trick-Or-Treat Daily status — surfaces the "🎃 sub-60s = 25/50☾"
  // banner during October and reflects whether today's claim was taken.
  const [tot, setTot] = useState(null);
  useEffect(() => {
    api.get("/trick-or-treat/status").then((r) => setTot(r.data)).catch(() => {});
  }, [data?.passed]);

  const onDailyFail = useCallback(() => {
    api.post("/daily/attempt-failed").then(() => {
      refreshCalendar();
      refreshToday();
    }).catch(() => {});
  }, [refreshCalendar, refreshToday]);

  const shareText = data
    ? `I scored ${(lastScore || data.best || 0).toLocaleString()} on Cosmic Worm Breaker — Daily Run ${data.seed}. Can you beat it?`
    : "";
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/daily` : "";

  const handleShare = async () => {
    const text = `${shareText}\n${shareUrl}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Cosmic Worm Breaker — Daily", text, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      }
    } catch (_) { /* cancelled */ }
  };

  if (!data || !dailyLevel) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 flex items-center justify-center min-h-screen font-mono-cosmic text-[#8b949e] tracking-widest">
          SYNCING DAILY RUN...
        </main>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-4 pt-24 pb-16 fade-in">
        <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 font-mono-cosmic text-[11px] tracking-[0.35em] text-[#ff007f] uppercase mb-2">
              <CalendarIcon className="w-3.5 h-3.5" />
              Daily Run · {data.seed}
            </div>
            <h1 className="font-heading font-black uppercase text-3xl sm:text-4xl tracking-tighter">
              One Seed. One Shot.
            </h1>
            <p className="text-white/60 text-sm mt-1">
              Retry until you clear it. Once you pass, the day locks — come back at 00:00 UTC for the next seed.
            </p>
          </div>
          <button onClick={handleShare} className="btn-neon cyan" data-testid="daily-share-btn">
            <span className="inline-flex items-center gap-2">
              {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {copied ? "Copied!" : "Share"}            </span>
          </button>
        </header>

        {/* Trick-Or-Treat Daily banner — surfaces during October (any
            year). Communicates the sub-60s bonus tiers so pilots know
            they can pick up 25/50 moonstones on a speed clear. Flips
            to a "claimed" chip after today's bonus lands. */}
        {tot?.active && (
          <div
            className="mb-4 flex flex-wrap items-center gap-3 px-3 py-2 border border-[#ff8a00]/70"
            style={{
              background:
                "linear-gradient(120deg, rgba(255,138,0,0.12) 0%, rgba(74,0,128,0.16) 100%)",
              boxShadow: "0 0 14px rgba(255,138,0,0.25)",
            }}
            data-testid="trick-or-treat-banner"
          >
            <span className="text-lg leading-none" aria-hidden>🎃</span>
            <div className="flex-1 min-w-[180px]">
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#ffb648]">
                // TRICK-OR-TREAT · October Bonus
              </div>
              <div className="text-white text-sm">
                Clear the daily under <span className="text-[#ffd700] font-heading">30s → +50 ☾</span> · under <span className="text-[#ffea3a] font-heading">60s → +25 ☾</span>
              </div>
            </div>
            {tot.claimed_today ? (
              <span
                className="inline-flex items-center gap-1 px-2 py-1 border border-[#00ff88]/60 text-[#00ff88] font-mono-cosmic text-[9px] tracking-[0.3em] uppercase"
                data-testid="trick-or-treat-claimed-badge"
              >
                <Check className="w-3 h-3" /> Claimed +{tot.claim?.reward || 0}☾
              </span>
            ) : (
              <span
                className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/60"
                data-testid="trick-or-treat-unclaimed"
              >
                Tip on today's clear
              </span>
            )}
          </div>
        )}

        {/* Streak + Shield status strip — earned via the 14-day Streak Shield
            legendary achievement. Auto-deploys on a missed day to preserve
            the streak. Hidden until the pilot has at least one streak day. */}
        {(streak.daily_streak > 0 || streak.streak_shields > 0) && (
          <div
            className="mb-6 flex flex-wrap items-center gap-2"
            data-testid="streak-strip"
          >
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#ff9500]/60 bg-[#ff9500]/5 text-[#ff9500] font-mono-cosmic text-[10px] tracking-[0.3em] uppercase"
              data-testid="streak-count"
              title="Consecutive UTC days you've cleared the Daily Run"
            >
              <Flame className="w-3.5 h-3.5" />
              {streak.daily_streak.toLocaleString()}-Day Streak
            </div>
            {streak.streak_shields > 0 ? (
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#b026ff]/60 bg-[#b026ff]/10 text-[#b026ff] font-mono-cosmic text-[10px] tracking-[0.3em] uppercase"
                data-testid="streak-shield-count"
                title="Auto-deploys on a missed day to preserve your streak"
                style={{ boxShadow: "0 0 12px rgba(176,38,255,0.35)" }}
              >
                <Shield className="w-3.5 h-3.5" />
                {streak.streak_shields} Shield{streak.streak_shields === 1 ? "" : "s"}
              </div>
            ) : (
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 text-white/40 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase"
                data-testid="streak-shield-locked"
                title="Reach a 14-day streak to forge a Streak Shield"
              >
                <Shield className="w-3.5 h-3.5" />
                {Math.max(0, 14 - streak.daily_streak)}d to next shield
              </div>
            )}
            {/* Personal global rank chip — links to the public Top Streaks
                board on /friends. Only renders when the pilot is actually
                on the board (rank != null), gold for podium positions. */}
            {streakRank.rank != null && (
              <Link
                to="/friends"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition ${
                  streakRank.rank <= 3
                    ? "border-[#ffd700] bg-[#ffd700]/10 text-[#ffd700] hover:bg-[#ffd700]/20"
                    : "border-[#00ffff]/40 bg-[#00ffff]/5 text-[#00ffff] hover:bg-[#00ffff]/10"
                }`}
                data-testid="streak-rank-chip"
                title="See the global Top Streak Pilots board"
                style={
                  streakRank.rank <= 3
                    ? { boxShadow: "0 0 12px rgba(255,215,0,0.35)" }
                    : undefined
                }
              >
                <Medal className="w-3.5 h-3.5" />
                Rank #{streakRank.rank.toLocaleString()}
                {streakRank.total_streakers > 0 && (
                  <span className="text-white/50">/ {streakRank.total_streakers.toLocaleString()}</span>
                )}
              </Link>
            )}
          </div>
        )}

        {/* "Streak at risk" banner — surfaces only when the pilot has a
            meaningful streak (≥3 days) AND zero shields in the bank. One-tap
            CTA to the Shield Store on /skins so they can insure their run
            before midnight UTC. Hidden once they have any shield charge. */}
        {streak.streak_shields === 0 && streak.daily_streak >= 3 && (
          <Link
            to="/skins#shield-store"
            className="mb-6 flex flex-wrap items-center gap-3 px-4 py-3 border border-[#ff007f]/60 bg-[#ff007f]/5 hover:bg-[#ff007f]/10 transition"
            style={{ boxShadow: "0 0 18px rgba(255,0,127,0.18)" }}
            data-testid="streak-at-risk-banner"
          >
            <AlertTriangle className="w-4 h-4 text-[#ff007f] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-heading uppercase tracking-tight text-sm text-white">
                Streak at risk
              </div>
              <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/60 mt-0.5">
                {streak.daily_streak}-day streak · 0 shields in the bank · insure it from $0.99
              </div>
            </div>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#ff007f] text-[#ff007f] font-mono-cosmic text-[10px] tracking-[0.3em] uppercase hover:bg-[#ff007f]/10 flex-shrink-0"
            >
              <ShoppingCart className="w-3 h-3" /> Buy Shields
            </span>
          </Link>
        )}

        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
          <div className="min-w-0">
            {data.locked ? (
              <LockedCard seed={data.seed} best={data.best} attempts={data.attempts} />
            ) : (
              <>
                {/* DAILY RESUME BANNER — softer recovery than Endless's:
                    bricks reset on resume but lives + score carry over.
                    Pilot's choice: continue with the saved progress or
                    restart fresh. Auto-clears on pass + on a different
                    UTC day server-side. */}
                {showDailyResumeCta && (
                  <div
                    className="mb-3 px-4 py-3 border border-[#ffd700]/40 bg-[#ffd700]/[0.06] flex flex-wrap items-center justify-between gap-3"
                    style={{ boxShadow: "0 0 14px rgba(255,215,0,0.18)" }}
                    data-testid="daily-resume-banner"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ffd700] uppercase">
                        // ATTEMPT RECOVERED
                      </div>
                      <div className="text-white/80 text-sm mt-0.5">
                        Carry over{" "}
                        <span className="text-[#ffd700] font-bold tabular-nums">
                          {dailyCheckpoint.score.toLocaleString()}
                        </span>{" "}
                        pts ·{" "}
                        <span className="text-[#ff66cc] font-bold tabular-nums">
                          {dailyCheckpoint.lives}
                        </span>{" "}
                        {dailyCheckpoint.lives === 1 ? "life" : "lives"} (bricks reset).
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={resumeFromDailyCheckpoint}
                        className="px-4 py-2 bg-[#ffd700] text-black font-mono-cosmic text-xs tracking-[0.25em] uppercase font-bold hover:bg-[#fff7d6] transition"
                        data-testid="daily-resume-continue"
                      >
                        Continue
                      </button>
                      <button
                        type="button"
                        onClick={dismissDailyCheckpoint}
                        className="px-3 py-2 border border-white/25 hover:border-white/60 text-white/70 hover:text-white font-mono-cosmic text-xs tracking-[0.25em] uppercase transition"
                        data-testid="daily-resume-dismiss"
                      >
                        Restart Fresh
                      </button>
                    </div>
                  </div>
                )}
                <GameCanvas
                  key={`${data.seed}-${canvasResumeKey}`}
                  level={1}
                  levelOverride={dailyLevel}
                  isDaily
                  dailyStartLives={
                    canvasResumeKey > 0 && dailyCheckpoint
                      ? dailyCheckpoint.lives
                      : null
                  }
                  dailyStartScore={
                    canvasResumeKey > 0 && dailyCheckpoint
                      ? dailyCheckpoint.score
                      : null
                  }
                  onComplete={onComplete}
                  onFail={onDailyFail}
                  onExit={() => navigate("/levels")}
                  bestScore={data.leaderboard?.[0]?.score || 0}
                />
              </>
            )}

            <MonthCalendar
              month={calMonth}
              days={calendar.days}
              onPrev={() =>
                setCalMonth((d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1)))
              }
              onNext={() =>
                setCalMonth((d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)))
              }
            />
          </div>

          <aside className="glass p-6 self-start" data-testid="daily-leaderboard">
            <div className="flex items-center gap-2 mb-4">
              <TrophyIcon className="w-4 h-4 text-[#ffd700]" />
              <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
                Today's Pilots
              </div>
            </div>
            {data.leaderboard.length === 0 ? (
              <div className="text-white/50 font-mono-cosmic text-xs tracking-widest uppercase">
                No scores yet. Set the first.
              </div>
            ) : (
              <ol className="space-y-2">
                {data.leaderboard.map((row, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 last:border-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className="font-heading font-black w-7 text-lg"
                        style={{ color: i === 0 ? "#ffd700" : i === 1 ? "#00ffff" : i === 2 ? "#ff007f" : "#8b949e" }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="truncate text-white/90 text-sm">{row.name}</span>
                    </div>
                    <span className="font-mono-cosmic text-[11px] tracking-widest text-[#00ffff]">
                      {row.score.toLocaleString()}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}

function LockedCard({ seed, best, attempts }) {
  return (
    <div
      className="glass p-6 sm:p-10 border border-[#00ff41]/50 flex flex-col items-center text-center w-full"
      style={{ boxShadow: "0 0 26px rgba(0,255,65,0.2)", minHeight: "min(480px, 80vh)" }}
      data-testid="daily-locked-card"
    >
      <div className="w-16 h-16 flex items-center justify-center border border-[#00ff41]/60 text-[#00ff41] mb-4">
        <Lock className="w-7 h-7" />
      </div>
      <div className="font-mono-cosmic text-[10px] tracking-[0.35em] text-[#00ff41] uppercase mb-2">
        // RUN {seed} CLEARED
      </div>
      <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl tracking-tighter text-white mb-3">
        Daily complete
      </h2>
      <p className="text-white/60 text-sm max-w-md mb-6">
        You cleared today's seed. Streak preserved.
        The next seed unlocks at 00:00 UTC.
      </p>
      <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
        <div className="glass px-3 py-2 border border-[#ffd700]/40">
          <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50">Best Score</div>
          <div className="font-heading font-black text-lg text-[#ffd700]">{(best || 0).toLocaleString()}</div>
        </div>
        <div className="glass px-3 py-2 border border-[#00ffff]/40">
          <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50">Attempts</div>
          <div className="font-heading font-black text-lg text-[#00ffff]">{attempts || 1}</div>
        </div>
      </div>
    </div>
  );
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function MonthCalendar({ month, days, onPrev, onNext }) {
  const year = month.getUTCFullYear();
  const m = month.getUTCMonth();
  const label = `${MONTHS[m]} ${year}`;
  // Key by "YYYY-MM-DD" for O(1) lookup
  const byDate = useMemo(() => {
    const map = new Map();
    days.forEach((d) => map.set(d.date, d));
    return map;
  }, [days]);
  const firstDow = new Date(Date.UTC(year, m, 1)).getUTCDay(); // 0..6
  const daysInMonth = new Date(Date.UTC(year, m + 1, 0)).getUTCDate();
  const today = todayKey();

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ key, day: d });
  }

  return (
    <section className="mt-6 glass p-5 border border-white/15" data-testid="daily-calendar">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono-cosmic text-[10px] tracking-[0.35em] text-[#00ffff] uppercase mb-1">
            // COSMIC CALENDAR
          </div>
          <div className="font-heading uppercase text-lg tracking-tight text-white" data-testid="daily-calendar-month">
            {label}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onPrev}
            className="w-8 h-8 border border-white/15 hover:border-white/50 flex items-center justify-center"
            data-testid="daily-calendar-prev"
          >
            <ChevronLeft className="w-4 h-4 text-white/70" />
          </button>
          <button
            onClick={onNext}
            className="w-8 h-8 border border-white/15 hover:border-white/50 flex items-center justify-center"
            data-testid="daily-calendar-next"
          >
            <ChevronRight className="w-4 h-4 text-white/70" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {["S","M","T","W","T","F","S"].map((d, i) => (
          <div key={i} className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/40 text-center">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="aspect-square" />;
          const row = byDate.get(c.key);
          const isToday = c.key === today;
          const passed = row?.passed;
          const tried = !!row;
          let bg = "rgba(255,255,255,0.05)";
          let border = "rgba(255,255,255,0.1)";
          let color = "#4d4d66";
          if (passed) {
            bg = "rgba(0,255,65,0.15)";
            border = "rgba(0,255,65,0.6)";
            color = "#00ff41";
          } else if (tried) {
            bg = "rgba(255,0,127,0.12)";
            border = "rgba(255,0,127,0.55)";
            color = "#ff80bf";
          }
          if (isToday) {
            border = "#00ffff";
          }
          return (
            <div
              key={i}
              className="aspect-square relative flex items-center justify-center border font-mono-cosmic text-[11px] tabular-nums"
              style={{ background: bg, borderColor: border, color }}
              title={row ? `${c.key} · ${passed ? "cleared" : "attempted"} · ${row.score.toLocaleString()} pts · ${row.attempts}x` : c.key}
              data-testid={`daily-cal-${c.key}${passed ? "-passed" : tried ? "-tried" : ""}`}
            >
              {c.day}
              {passed && (
                <Check className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 text-[#00ff41]" />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-4 flex-wrap font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 border border-[#00ff41]/60 bg-[#00ff41]/20" /> Cleared
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 border border-[#ff007f]/55 bg-[#ff007f]/12" /> Attempted
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 border border-[#00ffff] bg-transparent" /> Today
        </span>
      </div>
    </section>
  );
}
